import { registerClientPackage } from '@packages/clients';
import { type Container } from '@packages/core/container';

import * as AccountOperations from './accounts/operations';
import * as CvtOperations from './cvt/operations';
import * as DtcCodeOperations from './dtc-codes/operations';
import * as FailCodesOperations from './fail-codes/operations';
import * as Ssm5Operations from './ssm5/operations';
import * as TechlineAssetOperations from './techline/assets/operations';
import * as TechlineAssignmentOperations from './techline/assignments/operations';
import * as TechlineCaseOperations from './techline/cases/operations';
import * as TechlineCidCvnOperations from './techline/cid-cvn/operations';
import * as TechlineCommentOperations from './techline/comments/operations';
import * as TechlineDtcCodeOperations from './techline/dtc-codes/operations';
import * as TechlineMemberOperations from './techline/members/operations';
import * as TechlineVinOperations from './techline/vin/operations';
import * as TechlineWorksheetSubmissionOperations from './techline/worksheet-submissions/operations';
import * as WorksheetFormOperations from './worksheets/forms/operations';
import * as WorksheetGroupOperations from './worksheets/groups/operations';

/**
 * Register all domain operations from `@packages/domain` into the provided container.
 * All registrations are lazy; nothing is constructed until first `resolve()`.
 * The client package ({@link registerClientPackage}) must be registered first so that
 * operations can resolve their client dependencies.
 *
 * @param container - the container to register into
 * @returns the same container, with domain operation registrations added
 */
export function registerDomainPackage(container: Container): Container {
    // Register client dependencies first (idempotent if already registered)
    registerClientPackage(container);

    // Operations (self-registering via static registerInContainer)
    registerOperations(container, [
        AccountOperations,
        CvtOperations,
        DtcCodeOperations,
        FailCodesOperations,
        Ssm5Operations,
        TechlineAssetOperations,
        TechlineAssignmentOperations,
        TechlineCaseOperations,
        TechlineCidCvnOperations,
        TechlineCommentOperations,
        TechlineDtcCodeOperations,
        TechlineMemberOperations,
        TechlineVinOperations,
        TechlineWorksheetSubmissionOperations,
        WorksheetFormOperations,
        WorksheetGroupOperations,
    ]);

    return container;
}

/**
 * Scan barrel import objects for operation classes that have a static `registerInContainer`
 * method, and call it for each one found. This enables operations to self-register their
 * dependencies, so adding a new operation only requires creating the file and adding it
 * to the barrel.
 *
 * @param container - the container to register operations in
 * @param importObjects - barrel `import * as X` objects to scan
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any -- barrel import objects have no common type; we duck-type for registerInContainer at runtime
function registerOperations(container: Container, importObjects: Record<string, any>[]): void {
    for (const barrel of importObjects) {
        for (const exported of Object.values(barrel)) {
            if (typeof exported?.registerInContainer === 'function') {
                exported.registerInContainer(container);
            }
        }
    }
}
